import { Link } from "react-router-dom";
import { ThemeToggle } from "../components/ThemeToggle";
import { ArrowLeft } from "lucide-react";

function PageHeader() {
  return (
    <header className="border-b border-border">
      <div className="max-w-[920px] mx-auto px-6 md:px-8 h-14 flex items-center justify-between">
        <div className="flex items-baseline gap-3">
          <Link
            to="/"
            data-testid="back-home-link"
            className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.3em] text-muted hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-3 w-3" strokeWidth={1.5} />
            Chromaforge
          </Link>
          <span className="text-[10px] uppercase tracking-[0.3em] text-muted hidden md:inline">
            · About
          </span>
        </div>
        <ThemeToggle />
      </div>
    </header>
  );
}

const Section = ({ title, children }) => (
  <section className="space-y-3">
    <h2 className="text-sm font-bold uppercase tracking-[0.2em]">{title}</h2>
    <div className="text-sm leading-relaxed text-foreground/85 space-y-3">{children}</div>
  </section>
);

const SpecRow = ({ label, value }) => (
  <div className="grid grid-cols-[140px_1fr] py-2 border-b border-border last:border-b-0">
    <span className="text-[10px] uppercase tracking-[0.2em] text-muted">{label}</span>
    <span className="font-mono text-xs">{value}</span>
  </div>
);

export default function About() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageHeader />
      <main className="max-w-[920px] mx-auto px-6 md:px-8 py-10 md:py-14 space-y-10">
        <div className="space-y-3">
          <span className="text-[10px] font-medium uppercase tracking-[0.3em] text-muted">
            Made for print designers
          </span>
          <h1 className="text-4xl md:text-5xl font-semibold tracking-tight">
            About Chromaforge
          </h1>
          <p className="text-sm text-muted max-w-prose leading-relaxed">
            A precision colour studio for designers who care about how their work looks
            once it leaves the screen.
          </p>
        </div>

        <Section title="What it does">
          <p>
            Type a HEX value, get accurate RGB and CMYK readings, plus a side-by-side
            preview of how that colour will reproduce on three different output paths —
            a calibrated monitor, a professional offset press (FOGRA39 coated), and a
            consumer colour laser printer.
          </p>
          <p>
            Beyond the swatch, Chromaforge generates six classic harmony palettes
            (complementary, split-complementary, analogous, triadic, tetradic/square and
            double-complementary), suggests five CMYK-safe neighbour colours, and lets
            you push the active colour through tint, shade, tone, saturation, hue and
            brightness sliders with live print soft-proofing.
          </p>
        </Section>

        <Section title="Why it exists">
          <p>
            Most colour tools online stop at the screen. As a print designer, the question
            that actually matters is: <em>will this still look right on paper?</em> Bright
            sRGB colours often clip badly in CMYK, pastels mush together on consumer
            toner, and shadows pick up unwanted warm casts. Chromaforge surfaces those
            shifts <em>before</em> you commit to the press cheque.
          </p>
        </Section>

        <Section title="Under the hood">
          <div className="border border-border bg-surface">
            <div className="px-4">
              <SpecRow label="Press profile" value="FOGRA39 · Coated · GCR 35% · TIL 320%" />
              <SpecRow label="Press dot gain" value="13% on coated stock" />
              <SpecRow label="Home laser sim" value="−15% global sat · pastel + cream mute · warm shadows" />
              <SpecRow label="Colour spaces" value="sRGB · HSL · HSV · CMYK round-trip" />
              <SpecRow label="Rendering" value="Pure client-side · no server, no tracking of palettes" />
              <SpecRow label="Storage" value="Theme preference only · browser localStorage" />
            </div>
          </div>
          <p>
            All calculations run in your browser. Your colours never leave your device.
          </p>
        </Section>

        <Section title="Who built it">
          <p>
            Chromaforge is an independent project by a small team of designers and
            engineers who wanted a colour reference they could actually trust before
            sending artwork to print. Feedback, bug reports and palette requests are
            welcome at <span className="font-mono">hello@freecmyk.com</span>.
          </p>
        </Section>

        <footer className="pt-10 mt-6 border-t border-border flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <Link
            to="/"
            className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted hover:text-foreground transition-colors"
          >
            ← Back to Chromaforge
          </Link>
          <Link
            to="/privacy"
            className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted hover:text-foreground transition-colors"
          >
            Privacy Policy →
          </Link>
        </footer>
      </main>
    </div>
  );
}
